import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';
import runningIcon  from '../../assets/icons/running.png';
import cyclingIcon  from '../../assets/icons/cycling.png';
import swimIcon     from '../../assets/icons/swim.png';
import strengthIcon from '../../assets/icons/strength.png';

const API_BASE = `${(process.env.REACT_APP_API_URL || API_BASE_URL).replace(/\/+$/, '')}/api`;

function fmtTime(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtNum(n) {
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return Math.round(n * 10) / 10;
}

const PERIODS = ['week', 'month', 'year'];
const PERIOD_LABELS = { week: 'Semana', month: 'Mes', year: 'Año' };

const SPORTS = ['all', 'ciclismo', 'correr', 'nadar', 'fuerza'];
const SPORT_LABELS = { all: 'Todos', ciclismo: 'Ciclismo', correr: 'Running', nadar: 'Natación', fuerza: 'Fuerza' };
const SPORT_ICONS  = { all: null, ciclismo: cyclingIcon, correr: runningIcon, nadar: swimIcon, fuerza: strengthIcon };

const TrainingSummaryCard = ({ userId }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('week');
  const [sport,  setSport]  = useState('all');
  const [data,   setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    axios.get(`${API_BASE}/activities/training-summary/${userId}`, { params: { period, sport } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, period, sport]);

  return (
    <div style={{ background: 'var(--bs-body-bg, #fff)', border: '1px solid #e0e0e0', borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ width: 28, height: 28, borderRadius: 6, background: '#193255', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="bi bi-bar-chart-line" style={{ fontSize: 14, color: 'white' }} />
        </div>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--bs-body-color)' }}>Resumen de entrenamiento</span>
      </div>

      {/* Período */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {PERIODS.map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            style={{ flex: 1, fontSize: 12, padding: '4px 0', borderRadius: 6,
              border: `1px solid ${period === p ? '#193255' : '#dee2e6'}`,
              background: period === p ? '#193255' : 'transparent',
              color: period === p ? '#fff' : 'var(--bs-body-color)',
              cursor: 'pointer' }}>
            {PERIOD_LABELS[p]}
          </button>
        ))}
      </div>

      {/* Deporte */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
        {SPORTS.map(s => (
          <button key={s} onClick={() => setSport(s)}
            style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20,
              border: `1px solid ${sport === s ? '#193255' : '#dee2e6'}`,
              background: sport === s ? '#193255' : 'transparent',
              color: sport === s ? '#fff' : 'var(--bs-body-color)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            {SPORT_ICONS[s]
              ? <img src={SPORT_ICONS[s]} alt={s} style={{ width: 14, height: 14, filter: sport === s ? 'brightness(0) invert(1)' : 'none' }} />
              : <i className="bi bi-grid" style={{ fontSize: 12 }} />}
            {SPORT_LABELS[s]}
          </button>
        ))}
      </div>

      {/* Métricas */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 12, color: '#888' }}>Cargando...</div>
      ) : data ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
            {[
              { icon: 'bi-activity',       label: 'Actividades', value: data.count },
              { icon: 'bi-geo-alt',         label: 'Distancia',   value: fmtNum(data.distance) + ' km' },
              { icon: 'bi-clock',           label: 'Tiempo',      value: fmtTime(data.time) },
              { icon: 'bi-arrow-up-circle', label: 'Desnivel',    value: fmtNum(data.ascent) + ' m' },
            ].map(m => (
              <div key={m.label} style={{ background: '#f8f9fa', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
                  <i className={`bi ${m.icon}`} style={{ marginRight: 4 }} />{m.label}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--bs-body-color)' }}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* TSS y calorías */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <div style={{ flex: 1, background: '#f8f9fa', borderRadius: 8, padding: '6px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#888' }}>TSS</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{data.tss}</div>
            </div>
            <div style={{ flex: 1, background: '#f8f9fa', borderRadius: 8, padding: '6px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#888' }}>Calorías</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{fmtNum(data.calories)} kcal</div>
            </div>
          </div>

          {/* Fuentes */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {data.sources.garmin > 0 && (
              <span style={{ fontSize: 11, background: '#e8f4fd', color: '#0d6efd', padding: '2px 8px', borderRadius: 20, border: '1px solid #b8d4f5' }}>
                <i className="bi bi-watch" style={{ marginRight: 3 }} />Garmin · {data.sources.garmin}
              </span>
            )}
            {data.sources.strava > 0 && (
              <span style={{ fontSize: 11, background: '#fff0e8', color: '#fd5e00', padding: '2px 8px', borderRadius: 20, border: '1px solid #ffd0b8' }}>
                Strava · {data.sources.strava}
              </span>
            )}
            {data.sources.manual > 0 && (
              <span style={{ fontSize: 11, background: '#f0f0f0', color: '#666', padding: '2px 8px', borderRadius: 20, border: '1px solid #ddd' }}>
                Manual · {data.sources.manual}
              </span>
            )}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 12, color: '#888' }}>Sin datos disponibles</div>
      )}
    </div>
  );
};

export default TrainingSummaryCard;
